import { useAuth, type useUser } from "@grafit/frontend-auth/use-auth";
import { setPosthogUser } from "@grafit/shared/observability/analytics";
import { log, setSentryUser } from "@grafit/shared/observability/sentry-front";
import { loadPylonWidget, setChatWidget } from "@grafit/shared/services/pylon";
import { useEffect } from "react";
import { trpc } from "../utils/trpc";

export const useObservabilityWithUserProfile = (): void => {
  const auth = useAuth();
  const { data: pylonData, error: pylonError } = trpc.organisation.getPylonIdentityHash.useQuery(undefined, {
    enabled: !!auth.user,
  });

  // Sentry/PostHog - immediate
  useEffect(() => {
    const userProfile = auth.user ? getUserProfile(auth.user) : null;
    setSentryUser(userProfile);
    setPosthogUser(userProfile);
  }, [auth.user]);

  // Pylon - success path (with identity verification)
  useEffect(() => {
    if (auth.user && pylonData?.emailHash) {
      setChatWidget(auth.user, pylonData.emailHash);
      loadPylonWidget();
    }
  }, [auth.user, pylonData?.emailHash]);

  // Pylon - error fallback (without identity verification)
  useEffect(() => {
    if (auth.user && pylonError) {
      log(`Failed to fetch Pylon identity hash, initializing chat without verification ${pylonError.message}`, "warn");
      setChatWidget(auth.user, undefined);
      loadPylonWidget();
    }
  }, [auth.user, pylonError]);
};

type UseUserReturn = ReturnType<typeof useUser>;
interface UserProfile {
  id: string;
  email?: string;
}

const getUserProfile = (user: NonNullable<UseUserReturn>): UserProfile => {
  const email = user.email;
  const sentryUser = email ? { email, id: user.id } : { id: user.id };
  return sentryUser;
};
